import { toast } from "sonner";
import { z } from "zod";

import type { AuthResponse } from "@/types";

import { resendEmailVerification, verifyEmail } from "@/api/auth";
import { useUserProfile } from "@/api/users-query";
import useAuthStore from "@/store/auth";
import { useMutation } from "@tanstack/react-query";

export const verifySchema = z.object({
	otp: z.string(),
	hash: z.string(),
	email: z.string().email()

});
export type TVerifySchema = z.infer<typeof verifySchema>;

export interface ResendEmailResponse {
	message: string;
	isSuccessful: boolean;
	data: string;
}

export function useResendEmail() {
	interface ResendEmailParams {
		email: string;
		endpoint: string;
	}

	return useMutation<ResendEmailResponse, Error & { response: { data: { message: string } } }, ResendEmailParams>({
		mutationFn: ({ email, endpoint }: ResendEmailParams) => resendEmailVerification({ email }, endpoint),
		onError: (error) => {
			toast.error("Failed to send code", {
				description: error.response.data.message
			});
			console.error("Authentication Error:", error);
		}
	});
}

export function useVerifyEmail() {
	const profile = useUserProfile();

	const user = useAuthStore(state => state.user);

	if (!user)
		throw new Error("User cannot be null");
	const updateUser = useAuthStore(state => state.updateUser);
	return useMutation<AuthResponse, Error, { data: TVerifySchema }>({
		mutationFn: ({ data }) => verifyEmail(data),
		onSuccess: async () => {
			delete user?.hash;
			updateUser({ ...user, status: "ACTIVE" });
			profile.refetch();
		},
		onError: (error) => {
			console.error("Authentication Error:", error.message);
		}
	});
}
